import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchAllSnapshots } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import DualExplainer from "@/components/DualExplainer";

export default function Correlation() {
  const [data, setData] = useState(null);
  const [regionId, setRegionId] = useState("");

  useEffect(() => {
    (async () => {
      const d = await fetchAllSnapshots();
      setData(d);
      if (!regionId && d?.regions?.[0]) setRegionId(d.regions[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snap = data?.snapshots?.find((s) => s.region_id === regionId);
  const regions = data?.regions || [];

  const rows = useMemo(() => {
    if (!snap) return [];
    const dates = snap.satellite?.dates || [];
    const prices = snap.financial?.close || [];
    const priceByDate = {};
    (snap.financial?.dates || []).forEach((d, i) => (priceByDate[d] = prices[i]));
    return dates.map((date, i) => ({
      date: date?.slice(5),
      ndvi: snap.satellite.ndvi?.[i],
      sm: snap.weather?.soil_moisture_0_7cm?.[i],
      price: priceByDate[date] ?? null,
    }));
  }, [snap]);

  const m = snap?.metrics;

  return (
    <div className="space-y-6" data-testid="correlation-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Correlation Engine · Trading Overlay</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
            Makro-Ökonomie &times; Agronomie
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Sinkender NDVI × Bodenfeuchte-Defizit → Ertragsprognose → Preis-Sensitivität
            der korrespondierenden Futures.
          </p>
        </div>
        <div data-testid="region-selector">
          <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="correlation-region-select" />
        </div>
      </header>

      <DualExplainer
        title="Warum hängen Satellitendaten und Weltmarktpreise zusammen?"
        testid="correlation-explainer"
        simple={
          <>
            <p>
              Wenn deine Felder <strong>gelber werden</strong> (NDVI sinkt) oder der Boden
              <strong> austrocknet</strong>, passiert das selten nur auf deinem Hof — meist in
              der ganzen Region. Weniger grüne Blätter heißt: <strong>weniger Ertrag</strong> → 
              weniger Ware am Weltmarkt → <strong>steigende Preise</strong>.
            </p>
            <p>
              Diese Seite legt beides übereinander: die grüne Linie (Pflanzengesundheit) und
              die graue Linie (Börsenpreis). Bewegen sie sich gegenläufig, spricht das für
              den Zusammenhang.
            </p>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Verschlechtert sich der NDVI in deiner Region und
              der Preis zieht an, kann das ein früher Hinweis auf eine bessere Vermarktungs-
              gelegenheit sein — oder ein Warnsignal, dass auch bei dir Ertrag fehlen könnte.
              <em> Keine Handelsempfehlung.</em>
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Berechnung:</strong> Pearson-Korrelationskoeffizient ρ zwischen NDVI-Zeit-
              reihe (n=30, Sentinel-2) und Close-Preis des korrespondierenden Futures-Tickers
              (z. B. ZW=F für Weizen), ohne Lag-Korrektur.
            </p>
            <p>
              <strong>Limitationen:</strong> Pearson-ρ misst nur lineare Zusammenhänge. Für
              realistische Preis-Elastizitäten wären Granger-Kausalitätstests, DCC-GARCH
              für zeitvariante Korrelationen oder ein VAR-Modell mit Ernteberichts-Exogenen
              (USDA WASDE, MARS-Bulletins) angebracht. Nationale Futures reagieren auch auf
              Nicht-EU-Events (Trockenheit USA, Schwarzmeer, Monsun) — die hier gezeigte
              regionale NDVI-Korrelation ist daher <strong>kein kausaler Treiber</strong>,
              sondern ein lokaler Indikator.
            </p>
            <p>
              <strong>Interpretation:</strong> |ρ| &gt; 0.4 oft signifikant, &gt; 0.7 stark.
              Negative ρ erwartet bei defizitären Szenarien (fallender NDVI ↔ steigender Preis).
              p-Wert und Stichprobengröße werden derzeit nicht geprüft — für
              publikationsreife Analysen bitte auf Ebene der Rohdaten arbeiten.
            </p>
          </>
        }
      />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="eid-card p-4 lg:col-span-2" data-testid="overlay-chart-card">
          <div className="flex items-baseline justify-between">
            <h3 className="text-lg font-medium">Futures-Preis × NDVI × Bodenfeuchte</h3>
            <div className="eid-label">
              ρ(NDVI, Preis) = <span className="text-white">{m?.price_ndvi_correlation ?? "–"}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={rows} margin={{ top: 24, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
              <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <YAxis yAxisId="left" fontSize={10} tickLine={false} stroke="#8A8A93" domain={[0, "auto"]} />
              <YAxis yAxisId="right" orientation="right" fontSize={10} tickLine={false} stroke="#8A8A93" />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "none", fontFamily: "JetBrains Mono", fontSize: 11, color: "white" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="ndvi" name="NDVI" stroke="#00FA9A" strokeWidth={2} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="sm" name="Bodenfeuchte 0–10cm" stroke="#4DA3FF" strokeWidth={2} strokeDasharray="4 3" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="price" name="Futures-Preis" stroke="#FFC24B" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <aside className="eid-card p-4 space-y-4" data-testid="metrics-panel">
          <h3 className="text-lg font-medium">Metriken</h3>
          <Row label="NDVI aktuell" value={m?.ndvi_current ?? "–"} />
          <Row label="NDVI Ø 30d" value={m?.ndvi_mean_30d ?? "–"} />
          <Row
            label="NDVI Anomalie"
            value={`${m?.ndvi_anomaly >= 0 ? "+" : ""}${m?.ndvi_anomaly ?? "–"}`}
            tone={m?.ndvi_anomaly < 0 ? "warn" : "ok"}
          />
          <div className="eid-divider" />
          <Row label="Boden 0–10 cm" value={m?.soil_moisture_current ?? "–"} unit="m³/m³" />
          <Row
            label="Defizit"
            value={`${m?.soil_moisture_deficit ?? "–"}`}
            tone={m?.soil_moisture_deficit > 0 ? "warn" : "ok"}
          />
          <Row label="ET₀ 7d Summe" value={m?.et0_7d_sum ?? "–"} unit="mm" />
          <div className="eid-divider" />
          <div>
            <div className="eid-label">Ertragsprognose-Index</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="eid-value text-3xl">{m?.yield_forecast_index ?? "–"}</div>
              <span className="eid-label">/100</span>
            </div>
            <div className="h-2 bg-[#161616] mt-2 rounded-sm overflow-hidden">
              <div
                className="h-full bg-[#00FA9A]"
                style={{ width: `${Math.max(0, Math.min(100, m?.yield_forecast_index ?? 0))}%` }}
              />
            </div>
          </div>
          <div className="eid-divider" />
          <Row
            label="Futures aktuell"
            value={m?.price_current ?? "–"}
            unit={snap?.financial?.ticker}
          />
          <Row
            label="Preis Δ 7d"
            value={`${m?.price_change_7d_pct >= 0 ? "+" : ""}${m?.price_change_7d_pct ?? "–"}%`}
            tone={m?.price_change_7d_pct < 0 ? "warn" : "ok"}
          />
          <Row
            label="ρ(NDVI, Preis)"
            value={m?.price_ndvi_correlation ?? "–"}
          />
          <Row label="Quelle Preis" value={snap?.financial?.source || "–"} />
        </aside>
      </section>
    </div>
  );
}

function Row({ label, value, unit, tone }) {
  const toneCls =
    tone === "warn" ? "text-[#FF7A45]" : tone === "ok" ? "text-[#00FA9A]" : "text-white";
  return (
    <div className="flex items-baseline justify-between">
      <span className="eid-label">{label}</span>
      <span className={`font-mono text-sm ${toneCls}`}>
        {value} {unit && <span className="text-zinc-500 text-xs ml-1">{unit}</span>}
      </span>
    </div>
  );
}
